import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { FC, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useNotificationService } from 'src/features/notificationService/useNotificationService'
import { isHTTPError } from 'src/state/middleware/saga/handleHttpError'
import { useMutationCreateMaster } from '../../api/useMutationCreateMaster'
import { MASTER_NAME_MAX_LENGTH } from '../../constants'

type MasterAddModalProps = {
  open: boolean
  onClose: () => void
  existingNames: string[]
}

export const MasterAddModal: FC<MasterAddModalProps> = ({
  open,
  onClose,
  existingNames,
}) => {
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')
  const history = useHistory()
  const { showNotification } = useNotificationService()
  const { mutate, isPending } = useMutationCreateMaster()

  const validate = (): boolean => {
    const trimmed = name.trim()
    if (trimmed.length > MASTER_NAME_MAX_LENGTH) {
      setNameError(
        `マスタ名は${MASTER_NAME_MAX_LENGTH}文字以内で入力してください`,
      )
      return false
    }
    if (existingNames.includes(trimmed)) {
      setNameError('このマスタ名は既に使用されています')
      return false
    }
    setNameError('')
    return true
  }

  const handleSubmit = () => {
    if (!validate()) return
    mutate(
      { name: name.trim() },
      {
        onSuccess: data => {
          showNotification('マスタを作成しました')
          onClose()
          setName('')
          setNameError('')
          history.push(`/masters/${data.masterUuid}`)
        },
        onError: (error: unknown) => {
          const message =
            isHTTPError(error) &&
            error.status === 400 &&
            error.errors.length > 0
              ? error.errors.join('\n')
              : 'マスタの作成に失敗しました'
          showNotification(message, {
            notificationType: 'error',
          })
        },
      },
    )
  }

  const handleClose = () => {
    setName('')
    setNameError('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>マスタを追加</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1">マスタ名</Typography>
        <TextField
          autoFocus
          required
          fullWidth
          value={name}
          onChange={e => {
            setName(e.target.value)
            if (nameError) setNameError('')
          }}
          error={!!nameError}
          helperText={nameError}
          sx={{ mt: 1 }}
          data-testid="master-add-modal-name-input"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          キャンセル
        </Button>
        <Tooltip title={!name.trim() ? 'マスタ名を入力してください' : ''} arrow>
          <span>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isPending || !name.trim()}
              data-testid="master-add-modal-submit-button"
            >
              作成
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  )
}
